import React, { ChangeEvent, RefObject, useEffect, useRef, useState } from 'react';
import { FileDataParam } from '../../types/FileUploadParam';
import { InputProps } from '../../../../types/HtmlProps';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useOnFormReset } from '../../utils/useOnFormReset';
import { useValidateFileSelection } from './useValidateFileSelection';
import { AttachedFilesPreviewList } from '../../../components/AttachedFilesBase/partials/AttachedFilesPreviewList';
import { sendLiveRegionMessage } from '../../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import { BaseFileUpload, CombinedRefContent } from './BaseFileUpload';
import { windowLogger } from '../../../Logger/windowLogger';

type FileUpload = {
  headingLabel: string;
  dropzoneText?: string;
  helpText?: string;
  param: FileDataParam<string>;
  formRef: RefObject<HTMLFormElement | null>;
} & Omit<InputProps, 'id' | 'ref'>;

export function FileUpload({ dropzoneText, param, headingLabel, formRef, ...inputProps }: FileUpload) {
  const { t } = useSonTranslation('FileUpload');
  const [selection, setSelection] = useState<File[]>([]);
  const [focusLastElement, setFocusLastElement] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(undefined);
  const labelRef = useRef<HTMLLabelElement>(undefined);
  const combinedRef = useRef<CombinedRefContent>({
    inputRef: inputRef,
    labelRef: labelRef,
  });
  const onSelectedFilesChanged = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    labelRef.current?.focus();

    setFocusLastElement(false);
    setSelection(files);
  };

  const reset = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.files = null;
    }

    setFocusLastElement(false);
    setSelection([]);
  };

  const removeFile = (itemToRemove: File) => {
    const remaining = selection.filter(item => item !== itemToRemove);

    if (remaining.length === 0 && labelRef.current) {
      labelRef.current.focus();
    }

    setFocusLastElement(true);
    setSelection(remaining);
  };

  useOnFormReset(formRef, reset);
  const validationErrors = useValidateFileSelection(selection, param);

  useEffect(() => {
    if (validationErrors.length > 0) {
      // Set() only allowing unique values makes sure we don't join duplicate error strings
      const errorString = [...new Set(validationErrors)].join('\n');
      inputRef.current?.setCustomValidity(errorString);
    } else {
      inputRef.current?.setCustomValidity('');
    }
  }, [selection, validationErrors]);

  useEffect(() => {
    // hacky way to alter files
    // stackoverflow.com/questions/5632629/how-to-change-a-file-inputs-filelist-programmatically
    if (selection.length > 0 && inputRef.current) {
      const dataTransfer = new DataTransfer();
      selection.forEach(s => {
        dataTransfer.items.add(s);
      });
      try {
        inputRef.current.files = dataTransfer.files;
      } catch (e) {
        windowLogger.error('setting selection failed', e);
      }
    }
  }, [selection]);

  return (
    <BaseFileUpload
      {...inputProps}
      ref={combinedRef as any} // Typescript misinterprets the CombinedRefContent
      dropzoneText={dropzoneText}
      param={param}
      headingLabel={headingLabel}
      onSelectedFilesChanged={onSelectedFilesChanged}
    >
      {selection && selection.length > 0 ? (
        <AttachedFilesPreviewList
          selection={selection}
          hierarchy={2}
          focusLastElement={focusLastElement}
          onClick={file => {
            sendLiveRegionMessage(t('fileRemovedFromSelection', { fileName: file.name }));
            removeFile(file);
          }}
        />
      ) : null}
    </BaseFileUpload>
  );
}
